import { queryOptions } from "@tanstack/react-query";
import { BodyOf, QueryParamsOf } from "../../../core/api";
import { createQueryGroup } from "../../../core/api/queries";
import { ContractTypeId, PatientId, ServiceCodeId } from "../../../shared/schema/schema";
import {
  BulkScheduleAgencySettings,
  BulkScheduleContractTypeBillingRates,
  BulkScheduleDocumentType,
  BulkSchedulePayrollCode,
  BulkScheduleTaskTemplate,
} from "./bulk-schedule-tasks.types";

export default createQueryGroup(({ api }) => ({
  patients: (query: QueryParamsOf<"get", "./rn_scheduling_map">) =>
    queryOptions({
      queryKey: ["rnSchedulingMap", "patients", query],
      queryFn: () => api.get("./rn_scheduling_map", { query }),
      select: ({ patients }) => patients,
    }),

  counties: () =>
    queryOptions({
      queryKey: ["rnSchedulingMap", "counties"],
      queryFn: () => api.get("./rn_scheduling_map/counties", {}),
      select: ({ countyNames }) => countyNames,
    }),

  patientContracts: (patientId: PatientId) =>
    queryOptions({
      queryKey: ["rnSchedulingMap", "patientContracts", patientId],
      queryFn: () =>
        api.get("./patients/:patientId/contracts_basic", {
          path: { patientId },
          query: { include_general: false },
        }),
      select: ({ contracts }) => contracts,
    }),

  patientCertificationPeriods: (patientId: PatientId) =>
    queryOptions({
      queryKey: ["rnSchedulingMap", "patientCertificationPeriods", patientId],
      queryFn: () =>
        api.get("./patients/:patientId/certification_periods", { path: { patientId } }),
      select: ({ certificationPeriods }) => certificationPeriods,
    }),

  /** Overlap / gap check for a certification period the coordinator is about to create. */
  certificationPeriodAnomalies: (
    patientId: PatientId,
    body: BodyOf<"post", "./patients/:patientId/certification_periods/check_anomalies">
  ) =>
    queryOptions({
      queryKey: ["rnSchedulingMap", "certificationPeriodAnomalies", patientId, body],
      queryFn: () =>
        api.post("./patients/:patientId/certification_periods/check_anomalies", {
          path: { patientId },
          body,
        }),
    }),

  // Legacy router endpoints below are not in the generated schema.
  taskTemplates: () =>
    queryOptions({
      queryKey: ["rnSchedulingMap", "taskTemplates"],
      queryFn: () =>
        api.anyRequest<{ patientTaskTemplates: BulkScheduleTaskTemplate[] }>(
          "GET",
          "/agencies/:agencyId/agency_members/:agencyMemberId/patient_task_templates",
          {}
        ),
      select: ({ patientTaskTemplates }) => patientTaskTemplates,
    }),

  payrollCodes: () =>
    queryOptions({
      queryKey: ["rnSchedulingMap", "payrollCodes"],
      queryFn: () =>
        api.anyRequest<{ payrollCodes: BulkSchedulePayrollCode[] }>(
          "GET",
          "/agencies/:agencyId/agency_members/:agencyMemberId/payroll_codes",
          {}
        ),
      select: ({ payrollCodes }) => payrollCodes,
    }),

  documentTypes: () =>
    queryOptions({
      queryKey: ["rnSchedulingMap", "documentTypes"],
      queryFn: () =>
        api.anyRequest<{ documents: BulkScheduleDocumentType[] }>(
          "GET",
          "/agencies/:agencyId/:agencyMemberId/patient_document_types",
          {}
        ),
      // Same rule as the AngularJS modal: only titled documents with a published version.
      select: ({ documents }) =>
        documents.filter(
          (document) =>
            document.title !== "" && document.versions.some((version) => version.isPublished)
        ),
    }),

  agencySettings: () =>
    queryOptions({
      queryKey: ["rnSchedulingMap", "agencySettings"],
      queryFn: () =>
        api.anyRequest<BulkScheduleAgencySettings>("GET", "/agencies/:agencyId/settings", {}),
    }),

  /** Service codes that have a billing rate for each requested contract type. */
  contractTypeServiceCodes: (contractTypeIds: ContractTypeId[]) =>
    queryOptions({
      queryKey: ["rnSchedulingMap", "contractTypeServiceCodes", contractTypeIds],
      queryFn: () =>
        api.anyRequest<{ contractTypes: BulkScheduleContractTypeBillingRates[] }>(
          "GET",
          "/agencies/:agencyId/contracts/billing_rates",
          { query: { ids: contractTypeIds.join(",") } }
        ),
      select: ({ contractTypes }) => {
        const serviceCodesByContractType = new Map<ContractTypeId, Set<ServiceCodeId>>();

        for (const contractType of contractTypes) {
          const serviceCodeIds = contractType.billingRates.map((rate) => rate.serviceCode);

          if (serviceCodeIds.length > 0) {
            serviceCodesByContractType.set(contractType.contractTypeId, new Set(serviceCodeIds));
          }
        }

        return serviceCodesByContractType;
      },
      enabled: contractTypeIds.length > 0,
    }),
}));
